// File: TradeOfferPage.js (DROP-IN PAGE) — CASH-ONLY LAUNCH MODE
// ✅ Real routed page (no portal / no isOpen props)
// ✅ Keeps your tom2-* UI + TradeOfferModal.css look
// ✅ Fixes “buttons not clickable / textbox not input-ready” by rendering normally
// ✅ Uses PublicTrades for display, reads Trades only if allowed
// ✅ CASH-ONLY UI MODE:
//    - Hides swap offer option entirely
//    - Removes tradePreference messaging + offerType selector
//    - Always shows Price/Guide only
//    - Always submits CASH offers only
// ✅ Ready to re-enable swaps later by flipping ENABLE_SWAP_OFFERS to true.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./TradeOfferModal.css";

import { getTradeImageSrc, handleImgError } from "../utils/images";

/* =========================================================
   CASH-ONLY UI FLAGS
   - Flip ENABLE_SWAP_OFFERS when you’re ready to unhide swaps.
========================================================= */
const CASH_ONLY_UI = true;
const ENABLE_SWAP_OFFERS = false; // set true later to restore "trade" offers

/* -------------------- HELPERS -------------------- */
const normStr = (v) => String(v ?? "").trim();
const safeLower = (v) => String(v ?? "").trim().toLowerCase();

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const sanitizeMoneyInput = (v, { min = 1, max = 100000000 } = {}) => {
  const raw = String(v ?? "").replace(/[^\d]/g, "");
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  return String(Math.max(min, Math.min(max, n)));
};

const toMoneyNumber = (value) => {
  const n = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const roundToNearest = (value, step = 100) => {
  const n = Number(value);
  const s = Number(step);
  if (!Number.isFinite(n)) return 0;
  if (!Number.isFinite(s) || s <= 0) return Math.round(n);
  return Math.round(n / s) * s;
};

const getOfferReferenceAmount = (trade) => {
  return toMoneyNumber(
    trade?.expectedCashAmount ??
      trade?.estimatedValue ??
      trade?.price ??
      trade?.cashPrice ??
      trade?.askingPrice
  );
};

const getSellerMinimumOfferAmount = (trade) => {
  return toMoneyNumber(
    trade?.minimumOfferAmount ??
      trade?.minOfferAmount ??
      trade?.minimumAcceptableOffer ??
      trade?.minAcceptableOfferAmount
  );
};

const buildOfferGuard = ({ offerAmount, referenceAmount, sellerMinimumAmount }) => {
  const amt = toMoneyNumber(offerAmount);
  const ref = toMoneyNumber(referenceAmount);
  const sellerMin = toMoneyNumber(sellerMinimumAmount);

  if (!amt || !ref) {
    return {
      hasReference: false,
      blocked: false,
      warning: false,
      status: "no_reference",
      ratio: null,
      minAllowedAmount: 0,
      recommendedAmount: 0,
      message: "",
    };
  }

 const percentageMinimum = roundToNearest(ref * 0.6, 100);
  const minAllowedAmount = sellerMin > 0 ? sellerMin : percentageMinimum;
  const recommendedAmount = Math.max(
    roundToNearest(ref * 0.8, 100),
    minAllowedAmount
  );
  const ratio = amt / ref;

  if (amt < minAllowedAmount) {
    return {
      hasReference: true,
      blocked: true,
      warning: false,
      status: "blocked_low_offer",
      ratio,
      minAllowedAmount,
      recommendedAmount,
      message: `This offer is too low for this listing. Offers should start from around ${moneyZAR(
        minAllowedAmount
      )}.`,
    };
  }

  if (amt < recommendedAmount) {
    return {
      hasReference: true,
      blocked: false,
      warning: true,
      status: "below_recommended_range",
      ratio,
      minAllowedAmount,
      recommendedAmount,
      message: `This offer is below the seller’s expected range. Offers around ${moneyZAR(
        recommendedAmount
      )} or higher are more likely to be accepted.`,
    };
  }

  return {
    hasReference: true,
    blocked: false,
    warning: false,
    status: "within_expected_range",
    ratio,
    minAllowedAmount,
    recommendedAmount,
    message: "This offer is within a reasonable range for the listing.",
  };
};

const normalizeImages = (t) => {
  const val = t?.imageURLs ?? t?.imageUrls ?? t?.images ?? t?.imageURL ?? t?.imageUrl;
  const arr = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
  return arr
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .slice(0, 10);
};

// Keeps backend compatibility; UI does not surface preference messaging in cash-only mode.
const getTradePreference = (trade) => {
  const pref = normStr(trade?.tradePreference || "");
  if (pref === "trade_only" || pref === "cash_only" || pref === "trade_or_cash") return pref;

  const legacy = safeLower(trade?.allowCashTrade);
  if (legacy === "yes" || legacy === "true") return "trade_or_cash";
  if (legacy === "no" || legacy === "false") return "trade_only";

  return "trade_or_cash";
};

const hasRecentDuplicateOffer = async (intentKey) => {
  const qy = query(
    collection(db, "TradeOffers"),
    where("intentKey", "==", intentKey),
    where("status", "in", ["pending", "accepted"]),
    limit(1)
  );
  const snap = await getDocs(qy);
  return !snap.empty;
};

const useQuery = () => new URLSearchParams(useLocation().search);

const isProbablyAvailable = (t) => {
  if (!t) return false;
  if (t.isAvailable === false) return false;
  const st = safeLower(t.status);
  if (["closed", "sold", "cancelled", "canceled", "completed", "expired"].includes(st)) return false;
  return true;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/* -------------------- PAGE -------------------- */
export default function TradeOfferPage() {
  const { id } = useParams(); // target trade id
  const navigate = useNavigate();

  const location = useLocation();
  const qs = useQuery();
  const navState = useMemo(() => (location && location.state ? location.state : {}), [location]);

  const { currentUser } = useAuth();

  const firstFocusRef = useRef(null);
  const pageRef = useRef(null);

  const [targetTrade, setTargetTrade] = useState(null);
  const trade = targetTrade; // backward-compat alias

  // In cash-only UI, we don’t need qualifyingTrades for swaps, but keep loader for later re-enable.
  const [qualifyingTrades, setQualifyingTrades] = useState([]);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState({ type: "idle", text: "" });

  // SAFE NOTES (predefined only)
  const SAFE_NOTES = useMemo(
    () => [
      { id: "very_interested", label: "I’m very interested in this item" },
      { id: "ready_to_proceed", label: "Ready to proceed as soon as you accept" },
      { id: "confirm_available", label: "Please confirm the item is still available" },
      { id: "fair_offer", label: "This is my best/fair offer" },
      { id: "can_adjust", label: "I may adjust if you counter-offer" },
    ],
    []
  );

  // CASH-ONLY UI: always cash offer
  const [proposedCashAmount, setProposedCashAmount] = useState("");
  const [selectedNotes, setSelectedNotes] = useState([]);

  // preferredOfferType from URL/state is ignored in cash-only mode (kept for later)
  const preferredOfferType = useMemo(() => {
    const q = safeLower(qs.get("type"));
    if (q === "cash" || q === "trade") return q;

    const s = safeLower(navState?.preferredOfferType);
    if (s === "cash" || s === "trade") return s;

    return null;
  }, [qs, navState]);

  const pref = useMemo(() => getTradePreference(targetTrade), [targetTrade]);

  // In cash-only launch posture we force cash offers only (UI-wise).
  const allowCashTrade = CASH_ONLY_UI ? true : pref !== "trade_only";
  const canOfferCash = !!allowCashTrade;

  const canOfferTrade = useMemo(() => {
    if (!ENABLE_SWAP_OFFERS) return false;
    return Array.isArray(qualifyingTrades) && qualifyingTrades.length > 0;
  }, [qualifyingTrades]);

  // Cash-only: no selector
  const showOfferTypeSelector = !CASH_ONLY_UI && canOfferCash && canOfferTrade;

  const resolvedType = useMemo(() => {
    if (CASH_ONLY_UI) return "cash";
    if (preferredOfferType === "cash" && canOfferCash) return "cash";
    if (preferredOfferType === "trade" && canOfferTrade) return "trade";
    if (canOfferTrade) return "trade";
    if (canOfferCash) return "cash";
    return "cash";
  }, [preferredOfferType, canOfferCash, canOfferTrade]);

  // Load target trade
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!id) return;

        // Seed from navigation state (fast)
        if (navState?.targetTrade?.id && normStr(navState.targetTrade.id) === normStr(id)) {
          const seeded = {
            ...navState.targetTrade,
            id: normStr(navState.targetTrade.id),
            imageURLs: normalizeImages(navState.targetTrade),
            ownerId: normStr(navState.targetTrade.ownerId || navState.targetTrade.ownerUid) || null,
            ownerUid: normStr(navState.targetTrade.ownerUid || navState.targetTrade.ownerId) || null,
          };
          if (!cancelled) setTargetTrade(seeded);
        }

        const pubSnap = await getDoc(doc(db, "PublicTrades", id));
        if (!pubSnap.exists()) {
          if (!cancelled) setTargetTrade(null);
          return;
        }

        const publicData = pubSnap.data() || {};
        let canonicalData = null;

        if (currentUser?.uid) {
          try {
            const canonSnap = await getDoc(doc(db, "Trades", id));
            if (canonSnap.exists()) canonicalData = canonSnap.data() || null;
          } catch {
            // ignore if blocked
          }
        }

        const merged = { ...(canonicalData || {}), ...publicData };
        const images = normalizeImages(merged);

        const tradeWithId = {
          id: pubSnap.id,
          ...merged,
          imageURLs: images,
          ownerId: normStr(merged.ownerId || merged.ownerUid || merged.sellerUid) || null,
          ownerUid: normStr(merged.ownerUid || merged.ownerId || merged.sellerUid) || null,
        };

        if (!cancelled) setTargetTrade(tradeWithId);
      } catch (e) {
        console.error("Failed to load target trade:", e);
        if (!cancelled) setTargetTrade(null);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, currentUser?.uid, navState]);

  // Load qualifying trades (your items) — only if swaps are enabled
  useEffect(() => {
    let cancelled = false;

    if (!ENABLE_SWAP_OFFERS) {
      setQualifyingTrades([]);
      return () => {};
    }

    // Seed from navigation state if provided (fast path)
    if (Array.isArray(navState?.qualifyingTrades) && navState.qualifyingTrades.length) {
      const seeded = navState.qualifyingTrades
        .map((t) => ({ ...t, id: normStr(t.id), imageURLs: normalizeImages(t) }))
        .filter((t) => t.id && t.id !== id)
        .filter(isProbablyAvailable);

      if (!cancelled) setQualifyingTrades(seeded);
    }

    const loadMine = async () => {
      try {
        const myUid = normStr(currentUser?.uid);
        if (!myUid) {
          setQualifyingTrades([]);
          return;
        }

        // Try ownerId first
        let qsnap = await getDocs(
          query(collection(db, "Trades"), where("ownerId", "==", myUid), limit(60))
        );

        // fallback ownerUid
        if (qsnap.empty) {
          qsnap = await getDocs(
            query(collection(db, "Trades"), where("ownerUid", "==", myUid), limit(60))
          );
        }

        const mine = qsnap.docs
          .map((d) => ({ id: d.id, ...d.data(), imageURLs: normalizeImages(d.data()) }))
          .filter((t) => t.id !== id)
          .filter(isProbablyAvailable);

        if (!cancelled) setQualifyingTrades(mine);
      } catch (e) {
        console.error("Failed to load qualifying trades:", e);
        if (!cancelled) setQualifyingTrades([]);
      }
    };

    loadMine();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, id, navState]);

  // Reset on trade change
  useEffect(() => {
    setProposedCashAmount("");
    setSelectedNotes([]);
    setResult({ type: "idle", text: "" });
    setBusy(false);
  }, [targetTrade?.id]);

  // Focus first control
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      (firstFocusRef.current || pageRef.current)?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [resolvedType]);

  const toggleNote = (nid) => {
    setSelectedNotes((prev) => (prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]));
  };

  const buildSafeMessage = () => {
    const labels = SAFE_NOTES.filter((n) => selectedNotes.includes(n.id)).map((n) => n.label);
    return labels.length ? labels.join(" • ") : "";
  };

  const title = `Make a cash offer for: ${targetTrade?.title ?? "this item"}`;

  const estValue = targetTrade?.estimatedValue;

  const expectedCash =
  targetTrade?.expectedCashAmount ??
  targetTrade?.estimatedValue ??
  targetTrade?.price ??
  targetTrade?.cashPrice ??
  targetTrade?.askingPrice;

  const offerAmount = useMemo(
    () => toMoneyNumber(proposedCashAmount),
    [proposedCashAmount]
  );

  const offerReferenceAmount = useMemo(
    () => getOfferReferenceAmount(targetTrade),
    [targetTrade]
  );

  const sellerMinimumOfferAmount = useMemo(
    () => getSellerMinimumOfferAmount(targetTrade),
    [targetTrade]
  );

  const offerGuard = useMemo(
    () =>
      buildOfferGuard({
        offerAmount,
        referenceAmount: offerReferenceAmount,
        sellerMinimumAmount: sellerMinimumOfferAmount,
      }),
    [offerAmount, offerReferenceAmount, sellerMinimumOfferAmount]
  );

  const canSubmitCashOffer =
    !!canOfferCash && offerAmount > 0 && !offerGuard.blocked;

  const goBack = () => {
    const from = normStr(navState?.from);
    if (from) return navigate(from);
    if (targetTrade?.id) return navigate(`/trade/${targetTrade.id}`);
    return navigate("/");
  };

  const handleSubmit = async () => {
    if (busy) return;

    if (!currentUser?.uid) {
      toast.info("Please log in to make an offer.");
      navigate("/login", { state: { from: `/trade/${id}/offer?type=cash` } });
      return;
    }

    if (!targetTrade?.id) {
      toast.error("Trade not loaded. Please refresh and try again.");
      return;
    }

    // CASH ONLY: require cash amount
    const amt = toMoneyNumber(proposedCashAmount);

      if (!amt || amt <= 0) {
        setResult({ type: "err", text: "Please enter a cash amount greater than 0." });
        return;
      }

      if (offerGuard.blocked) {
        setResult({
          type: "err",
          text:
            offerGuard.message ||
            "This offer is too low for the seller’s expected range. Please increase your offer.",
        });
        return;
      }
    try {
      setBusy(true);
      setResult({ type: "idle", text: "" });

      const buyerUid = normStr(currentUser.uid);
      const tradeId = normStr(targetTrade.id);
      const sellerUid = normStr(targetTrade.ownerId || targetTrade.ownerUid || targetTrade.sellerUid);

      if (!sellerUid) {
        toast.error("Seller not found for this trade. Please refresh and try again.");
        return;
      }
      if (sellerUid === buyerUid) {
        toast.error("You cannot make an offer on your own trade.");
        return;
      }

      const intentKey = `${tradeId}__CASH__${buyerUid}`;

      // Best-effort dedupe
      try {
        const exists = await hasRecentDuplicateOffer(intentKey);
        if (exists) {
          toast.error("You already have a pending offer for this item.");
          return;
        }
      } catch {
        // ignore
      }

      const safeMsg = normStr(buildSafeMessage()).slice(0, 140);

      await addDoc(collection(db, "TradeOffers"), {
        // target trade
        tradeId,
        requestedItemId: tradeId,

        // actors (PII-free)
        buyerUid,
        fromUid: buyerUid,
        sellerUid,
        recipientUid: sellerUid,
        toUid: sellerUid,

        // payload
        offerType: "cash",
        offeredTradeId: null,
        offeredItemId: null,
        proposedCashAmount: amt,
        message: safeMsg || "",

        // offer guard metadata
        listingExpectedAmountZar: offerReferenceAmount || null,
        sellerMinimumOfferAmountZar: sellerMinimumOfferAmount || null,
        minimumAllowedOfferAmountZar: offerGuard.minAllowedAmount || null,
        recommendedOfferAmountZar: offerGuard.recommendedAmount || null,
        offerToListingRatio:
          typeof offerGuard.ratio === "number"
            ? Math.round(offerGuard.ratio * 10000) / 10000
            : null,
        offerGuardStatus: offerGuard.status || null,
        offerGuardVersion: 1,

        // status
        intentKey,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt: addDays(new Date(), 7),
          expiryPolicy: {
            type: "pending_offer_ttl",
            days: 7,
          },
      });

      toast.success("✅ Cash offer sent successfully!");
      setResult({ type: "ok", text: "Cash offer sent successfully." });

     setTimeout(() => {
        navigate(`/trade/${targetTrade.id}`, {
          replace: true,
          state: {
            offerJustCreated: true,
            tradeId: targetTrade.id,
          },
        });
      }, 700);
    } catch (e) {
      console.error("Offer submit failed:", e);
      setResult({ type: "err", text: e?.message || "Failed to send offer. Please try again." });
      toast.error("There was a problem sending your offer. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // If trade not found/loading
  if (!targetTrade) {
    return (
      <div className="tom2-overlay" style={{ background: "transparent", backdropFilter: "none" }}>
        <div className="tom2-shell" style={{ maxWidth: 820 }} tabIndex={-1} ref={pageRef}>
          <div className="tom2-topbar">
            <div>
              <div className="tom2-kicker">Make an offer</div>
              <h2 className="tom2-title">Loading…</h2>
              <div className="tom2-subtitle">Fetching trade details.</div>
            </div>
            <button className="tom2-close" type="button" onClick={goBack} aria-label="Back">
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CASH ONLY UI: if listing is explicitly trade_only (legacy), we still hide swaps and block cash offers.
  // If you want to *force* cash offers regardless, remove this block.
  if (CASH_ONLY_UI && pref === "trade_only") {
    return (
      <div className="tom2-overlay" role="main">
        <div className="tom2-shell" ref={pageRef} tabIndex={-1}>
          <div className="tom2-topbar">
            <div>
              <div className="tom2-kicker">Make an offer</div>
              <h2 className="tom2-title">Cash offers not available</h2>
              <div className="tom2-subtitle">
                This listing is not accepting cash offers at the moment.
              </div>
            </div>
            <button className="tom2-close" type="button" onClick={goBack} aria-label="Back">
              ✕
            </button>
          </div>

          <div className="tom2-section">
            <div className="tom2-fallback">
              <div className="tom2-fallback-title">Offer options are limited</div>
              <div className="tom2-fallback-text">
                Swaps are temporarily disabled and this listing is marked as trade-only.
              </div>

              <div className="tom2-fallback-actions">
                <button type="button" className="tom2-ghost" onClick={goBack}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tom2-overlay" role="main">
      <div
        className="tom2-shell"
        ref={pageRef}
        tabIndex={-1}
        role="region"
        aria-label="Make an offer page"
      >
        <div className="tom2-topbar">
          <div>
            <div className="tom2-kicker">Make an offer</div>
            <h2 className="tom2-title">{title}</h2>
            <div className="tom2-subtitle">No phone numbers, emails or private details are shared in offers.</div>
          </div>

          <button className="tom2-close" type="button" onClick={goBack} aria-label="Back">
            ✕
          </button>
        </div>

        <div className="tom2-target">
          <div className="tom2-target-media">
            <img
              className="tom2-target-img"
              src={getTradeImageSrc(targetTrade)}
              alt={targetTrade?.title || "Trade"}
              onError={handleImgError}
              loading="lazy"
            />
          </div>

          <div>
            <div className="tom2-target-title">{targetTrade?.title ?? "Item"}</div>

            <div className="tom2-target-meta">
              <div className="tom2-meta-pill">
                <span className="tom2-meta-label">Category</span>
                <span className="tom2-meta-value">{targetTrade?.category ?? "N/A"}</span>
              </div>

              {/* CASH ONLY UI: show Price/Guide only (no deal-type / preference messaging) */}
              <div className="tom2-meta-pill">
                <span className="tom2-meta-label">Price</span>
                <span className="tom2-meta-value">
                  {expectedCash != null ? moneyZAR(expectedCash) : "N/A"}
                </span>
              </div>

              {/* Keep est value optional as a neutral reference, but not “deal type” */}
              {estValue != null ? (
                <div className="tom2-meta-pill">
                  <span className="tom2-meta-label">Est. value</span>
                  <span className="tom2-meta-value">{moneyZAR(estValue)}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {result.text ? (
          <div className={`tom2-banner ${result.type === "ok" ? "ok" : "err"}`}>{result.text}</div>
        ) : null}

        {/* CASH ONLY UI: offer selector removed */}
        {showOfferTypeSelector ? null : null}

        {/* CASH ONLY UI: only cash input */}
        {canOfferCash && resolvedType === "cash" && (
          <div className="tom2-section">
            <div className="tom2-section-head">
              <div className="tom2-section-title">Your cash offer</div>
              <div className="tom2-section-hint">You can counter later if needed</div>
            </div>

            <div className="tom2-field">
              <div className="tom2-cash">
                <div className="tom2-cash-inputWrap">
                  <span className="tom2-currency">R</span>
                  <input
                    className="tom2-cash-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 6000"
                    value={proposedCashAmount}
                    onChange={(e) => setProposedCashAmount(sanitizeMoneyInput(e.target.value))}
                    disabled={busy}
                    ref={firstFocusRef}
                  />
                </div>

                <div className="tom2-cash-preview">
                  {proposedCashAmount ? moneyZAR(Number(proposedCashAmount)) : "—"}
                </div>

                {offerReferenceAmount > 0 ? (
                  <div className="tom2-offer-range">
                    <div className="tom2-offer-range__row">
                      <span>Seller guide price</span>
                      <strong>{moneyZAR(offerReferenceAmount)}</strong>
                    </div>

                    <div className="tom2-offer-range__row">
                      <span>Minimum offer range</span>
                      <strong>{moneyZAR(offerGuard.minAllowedAmount)}</strong>
                    </div>

                    <div className="tom2-offer-range__row">
                      <span>Recommended offer range</span>
                      <strong>{moneyZAR(offerGuard.recommendedAmount)}+</strong>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="tom2-muted">Avoid sharing personal contact details. NexterZone mediates the exchange.</div>
              {offerAmount > 0 && offerGuard.hasReference ? (
                <div
                  className={`tom2-offer-guard ${
                    offerGuard.blocked
                      ? "is-blocked"
                      : offerGuard.warning
                      ? "is-warning"
                      : "is-good"
                  }`}
                >
                  {offerGuard.message}
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div className="tom2-section">
          <div className="tom2-section-head">
            <div className="tom2-section-title">Optional offer notes</div>
            <div className="tom2-section-hint">Offer-only (no meetup or contact notes)</div>
          </div>

          <div className="tom2-chips" aria-label="Offer notes">
            {SAFE_NOTES.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`tom2-chip ${selectedNotes.includes(n.id) ? "active" : ""}`}
                onClick={() => toggleNote(n.id)}
                disabled={busy}
              >
                {n.label}
              </button>
            ))}
          </div>

          <div className="tom2-muted">Predefined only—no free text—so offers remain safe.</div>
        </div>

        <div className="tom2-actions">
          <button
            type="button"
            className="tom2-primary"
            onClick={handleSubmit}
            disabled={busy || !canSubmitCashOffer}
          >
            {busy ? "Sending…" : "💸 Send cash offer"}
          </button>

          <button type="button" className="tom2-ghost" onClick={goBack} disabled={busy}>
            Cancel
          </button>
        </div>

        {!canOfferCash ? (
          <div className="tom2-section">
            <div className="tom2-fallback">
              <div className="tom2-fallback-title">Cash offers are unavailable</div>
              <div className="tom2-fallback-text">
                This listing is not accepting cash offers at the moment.
              </div>

              <div className="tom2-fallback-actions">
                <button type="button" className="tom2-ghost" onClick={goBack}>
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
